import React, { useState } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Grid,
  Button,
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText
} from '@mui/material';
import { useAuth } from '../../lib/supabase/AuthContext';
import { updateCampaignQuestionnaire } from '../../lib/supabase/campaignsService';

// Structure des données du questionnaire
interface QuestionnaireData {
  // Informations de base
  campaignName?: string;
  website?: string;
  
  // Ciblage
  targetSector?: string;
  targetEmployeeCount?: string;
  targetCountry?: string;
  exclusionList?: string;
  targetCompanyExamples?: string;
  targetEmployees?: string;
  
  // Présentation de l'entreprise
  mainServices?: string;
  solvedProblem?: string;
  
  // Offre
  campaignOffer?: string;
  customerBenefits?: string;
  
  // Positionnement & Différenciation
  commonObjections?: string;
  differentiators?: string;
  mainCompetitors?: string;
  
  // Preuves & Chiffres clés
  customerReferences?: string;
  keyMetrics?: string;
}

interface CampaignQuestionnaireProps {
  campaignId: string;
  initialData?: QuestionnaireData;
  onSave?: () => void;
}

const CampaignQuestionnaire: React.FC<CampaignQuestionnaireProps> = ({
  campaignId,
  initialData = {},
  onSave
}) => {
  const { user } = useAuth();
  const [questionnaireData, setQuestionnaireData] = useState<QuestionnaireData>(initialData);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (field: keyof QuestionnaireData, value: string) => {
    setQuestionnaireData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Réinitialiser les messages d'état
    setError(null);
    setSuccess(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setError('Vous devez être connecté pour sauvegarder les données du questionnaire');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(false);
      
      const { error: updateError } = await updateCampaignQuestionnaire(
        campaignId,
        questionnaireData,
        user.id
      );
      
      if (updateError) {
        throw updateError;
      }
      
      setSuccess(true);
      if (onSave) {
        onSave();
      }
    } catch (err: any) {
      console.error('Erreur lors de la sauvegarde du questionnaire:', err);
      setError(`Une erreur est survenue: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
      <Typography variant="h5" gutterBottom>
        Questionnaire de campagne
      </Typography>
      
      <Typography variant="body2" color="text.secondary" paragraph>
        Veuillez remplir ce questionnaire pour nous aider à comprendre vos besoins et objectifs. 
        Ces informations nous permettront de personnaliser votre campagne.
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Les données du questionnaire ont été enregistrées avec succès.
        </Alert>
      )}
      
      <Box component="form" onSubmit={handleSubmit}>
        {/* Section 1: Informations de base */}
        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Informations de base
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Nom de la campagne"
              value={questionnaireData.campaignName || ''}
              onChange={(e) => handleChange('campaignName', e.target.value)}
              helperText="Donnez un nom à votre campagne"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Votre site internet"
              value={questionnaireData.website || ''}
              onChange={(e) => handleChange('website', e.target.value)}
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Section 2: Ciblage */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Ciblage
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Secteur cible"
              value={questionnaireData.targetSector || ''}
              onChange={(e) => handleChange('targetSector', e.target.value)}
              helperText="Ex: Finance, Santé, Éducation..."
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Nombre d'employés</InputLabel>
              <Select
                value={questionnaireData.targetEmployeeCount || ''}
                label="Nombre d'employés"
                onChange={(e) => handleChange('targetEmployeeCount', e.target.value)}
              >
                <MenuItem value="1-10">1-10</MenuItem>
                <MenuItem value="11-50">11-50</MenuItem>
                <MenuItem value="51-200">51-200</MenuItem>
                <MenuItem value="201-500">201-500</MenuItem>
                <MenuItem value="501-1000">501-1000</MenuItem>
                <MenuItem value="1001+">1001+</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Pays cible"
              value={questionnaireData.targetCountry || ''}
              onChange={(e) => handleChange('targetCountry', e.target.value)}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Liste d'exclusion"
              value={questionnaireData.exclusionList || ''}
              onChange={(e) => handleChange('exclusionList', e.target.value)}
              multiline
              rows={2}
              helperText="Lien Google Sheet contenant la liste des noms de domaine à exclure"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Exemples d'entreprises cibles"
              value={questionnaireData.targetCompanyExamples || ''}
              onChange={(e) => handleChange('targetCompanyExamples', e.target.value)}
              multiline
              rows={3}
              helperText="Listez quelques exemples d'entreprises que vous souhaitez cibler"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Employés cibles"
              value={questionnaireData.targetEmployees || ''}
              onChange={(e) => handleChange('targetEmployees', e.target.value)}
              multiline
              rows={3}
              helperText="Décrivez les profils des personnes que nous devrions contacter"
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Section 3: Présentation de l'entreprise */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Présentation de votre entreprise
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Services/Produits principaux"
              value={questionnaireData.mainServices || ''}
              onChange={(e) => handleChange('mainServices', e.target.value)}
              multiline
              rows={3}
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Problème résolu"
              value={questionnaireData.solvedProblem || ''}
              onChange={(e) => handleChange('solvedProblem', e.target.value)}
              multiline
              rows={3}
              helperText="Quel problème résolvez-vous pour vos clients ?"
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Section 4: Votre offre */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Votre offre
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Offre de la campagne"
              value={questionnaireData.campaignOffer || ''}
              onChange={(e) => handleChange('campaignOffer', e.target.value)}
              multiline
              rows={3}
              helperText="Décrivez l'offre que vous souhaitez mettre en avant"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Bénéfices clients"
              value={questionnaireData.customerBenefits || ''}
              onChange={(e) => handleChange('customerBenefits', e.target.value)}
              multiline
              rows={3}
              helperText="Quels bénéfices concrets vos clients obtiennent-ils ?"
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Section 5: Positionnement & Différenciation */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Positionnement & Différenciation
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Objections fréquentes"
              value={questionnaireData.commonObjections || ''}
              onChange={(e) => handleChange('commonObjections', e.target.value)}
              multiline
              rows={3}
              helperText="Quelles objections entendez-vous le plus souvent ?"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Éléments différenciateurs"
              value={questionnaireData.differentiators || ''}
              onChange={(e) => handleChange('differentiators', e.target.value)}
              multiline
              rows={3}
              helperText="Qu'est-ce qui vous différencie de vos concurrents ?"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Principaux concurrents"
              value={questionnaireData.mainCompetitors || ''}
              onChange={(e) => handleChange('mainCompetitors', e.target.value)}
              multiline
              rows={2}
            />
          </Grid>
        </Grid>
        
        <Divider sx={{ my: 3 }} />
        
        {/* Section 6: Preuves & Chiffres clés */}
        <Typography variant="h6" sx={{ mb: 2 }}>
          Preuves & Chiffres clés
        </Typography>
        
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Références clients"
              value={questionnaireData.customerReferences || ''}
              onChange={(e) => handleChange('customerReferences', e.target.value)}
              multiline
              rows={3}
              helperText="Listez vos références clients ou ajoutez des liens"
            />
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Chiffres clés"
              value={questionnaireData.keyMetrics || ''}
              onChange={(e) => handleChange('keyMetrics', e.target.value)}
              multiline
              rows={3}
              helperText="Partagez vos chiffres clés (nombre de clients, résultats, ancienneté, etc.)"
            />
          </Grid>
        </Grid>
        
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            size="large"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Enregistrer les données'}
          </Button>
        </Box>
      </Box>
    </Paper>
  );
};

export default CampaignQuestionnaire; 